// @ts-nocheck
import { useCallback, useEffect, useState } from 'react';

import { getUserCredit } from '@/components/UIComponents/CreditCard/checkCreditBalance';
import UserStore from '@/store/User';

import { getCookie } from '../utils';

// Intercom 配置常量
const INTERCOM_APP_ID = 'hugxlyj6';
const INTERCOM_API_BASE = 'https://api-iam.intercom.io';
const INTERCOM_WIDGET_URL = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;

// Intercom 设置类型定义
interface IntercomSettings {
  app_id: string;
  api_base: string;
  custom_launcher_selector: string;
  user_id?: string;
  name?: string;
  plan?: string;
  plan_is_expire?: boolean;
  credits?: number;
  plan_start_at?: number;
  plan_end_at?: number;
  [key: string]: any;
}

// 扩展 Window 类型
declare global {
  interface Window {
    Intercom?: any;
    intercomSettings?: IntercomSettings;
    attachEvent?: (event: string, listener: EventListener) => void;
  }
}

/**
 * Intercom 客服集成 Hook
 * 在用户交互时（鼠标悬停、滚动、触摸）懒加载 Intercom 脚本
 * @returns [isReport] - 是否已加载 Intercom
 */
export const useInterCom = (): [boolean] => {
  const [isReport, setIsReport] = useState<boolean>(false);

  // 只在生产环境启用 Intercom
  // 因为本地 localhost 域名未在 Intercom 后台配置，会返回 403
  // const isProduction = process.env.NODE_ENV === 'production';
  const isProduction = true;

  /**
   * 加载 Intercom 脚本
   */
  const loadIntercomScript = useCallback(() => {
    if (typeof window.Intercom === 'function') {
      // Intercom 已存在，重新激活
      window.Intercom('reattach_activator');
      window.Intercom('update', window.intercomSettings);
      return;
    }

    // 创建 Intercom 占位函数
    const intercomPlaceholder = function (this: any, ...args: any[]) {
      intercomPlaceholder.c(args);
    };
    intercomPlaceholder.q = [];
    intercomPlaceholder.c = function (args: any) {
      intercomPlaceholder.q.push(args);
    };
    window.Intercom = intercomPlaceholder;

    // 加载 Intercom 脚本
    const loadScript = () => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = INTERCOM_WIDGET_URL;

      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(script, firstScript);
    };

    // 根据文档加载状态决定何时加载脚本
    if (document.readyState === 'complete') {
      loadScript();
    } else if (window.attachEvent) {
      // IE 兼容
      window.attachEvent('onload', loadScript);
    } else {
      window.addEventListener('load', loadScript, false);
    }
  }, []);

  /**
   * 获取用户信息并配置 Intercom
   */
  const getUserInfoAndSetupIntercom = useCallback(async () => {
    const uid = getCookie('user')?.uid || '';
    const name = getCookie('user')?.email || getCookie('user')?.accountId || '';

    // 基础配置
    const baseSettings: IntercomSettings = {
      app_id: INTERCOM_APP_ID,
      api_base: INTERCOM_API_BASE,
      custom_launcher_selector: '#my_custom_link',
    };

    // 未登录用户
    if (!name && !uid) {
      baseSettings.plan = 'Guest';
      window.intercomSettings = baseSettings;
      return;
    }

    // 添加用户 ID
    if (uid) {
      baseSettings.user_id = uid;
    }

    // 添加用户名
    if (name) {
      baseSettings.name = name;
    }

    // 已登录用户 - 获取详细信息
    try {
      // 获取用户会员信息
      // 如果 currentVip 已经请求完成，直接使用；否则等待请求完成
      const currentVip = UserStore.getState().currentVip;
      const userInfo = currentVip.isRequestEnd
        ? currentVip
        : await UserStore.getState().getUserBenefit();

      // 设置用户计划信息
      baseSettings.plan = userInfo.key;
      baseSettings.plan_is_expire = userInfo.isExpire;
      baseSettings.credits = userInfo.balance;
      baseSettings.plan_start_at = userInfo.startTime
        ? userInfo.startTime / 1000
        : undefined;
      baseSettings.plan_end_at = userInfo.endTime ? userInfo.endTime / 1000 : undefined;

      // 获取订单来源信息（payment_id, redeem, configure）
      // 这些信息用于 Intercom 中追踪用户的订单来源
      const userCreditInfo = await getUserCredit();
      if (userCreditInfo) {
        Object.assign(baseSettings, userCreditInfo);
      }
    } catch (error) {
      console.error('Failed to fetch user info for Intercom:', error);
    }

    window.intercomSettings = baseSettings;
  }, []);

  /**
   * 初始化 Intercom
   */
  const initIntercom = useCallback(async () => {
    if (isReport) return;

    await getUserInfoAndSetupIntercom();
    loadIntercomScript();
    setIsReport(true);
  }, [isReport, getUserInfoAndSetupIntercom, loadIntercomScript]);

  useEffect(() => {
    // 如果已加载或不是生产环境，不执行
    if (isReport || !isProduction) return;

    // 在用户交互时懒加载 Intercom
    const events = ['mouseover', 'scroll', 'touchstart'] as const;

    events.forEach((event) => {
      document.addEventListener(event, initIntercom, { once: true });
    });

    // 清理事件监听器
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, initIntercom);
      });
    };
  }, [isReport, isProduction, initIntercom]);

  return [isReport];
};
